import { useEffect, useState } from "react";
import { CaseCard } from "@/components/radnici/ProcessCaseUI";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/api";
import { localToday } from "@/lib/permitProcesses";
import { centsString, groupSiteCosts, invoiceCosts } from "@/lib/invoiceCosts";
import { invoiceMoney } from "@/lib/receivedInvoices";

export function SiteCostsPanel() {
  const [month, setMonth] = useState(() => localToday().slice(0, 7));
  const [result, setResult] = useState<{
    month: string;
    sites: ReturnType<typeof groupSiteCosts>;
    error: string;
  } | null>(null);
  const [reload, setReload] = useState(0);
  const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(month) && !month.startsWith("0000");
  useEffect(() => {
    if (!valid) return;
    const controller = new AbortController();
    invoiceCosts
      .list(month, controller.signal)
      .then((rows) => {
        const sites = groupSiteCosts(rows);
        if (!controller.signal.aborted) setResult({ month, sites, error: "" });
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setResult({ month, sites: [], error: errorMessage(cause, "Troškovi nisu učitani.") });
      });
    return () => controller.abort();
  }, [month, valid, reload]);
  const loading = valid && result?.month !== month;
  return (
    <CaseCard title="Trošak po gradilištu">
      <label className="max-w-xs">
        Mjesec troška
        <input
          type="month"
          value={month}
          min="0001-01"
          max="9999-12"
          onChange={(event) => {
            setMonth(event.target.value);
            setResult(null);
          }}
        />
      </label>
      <p className="text-sm">Likvidirano · bez PDV-a · EUR</p>
      {!valid && <p role="alert">Odaberite valjani mjesec troška.</p>}
      {loading && <p role="status">Učitavanje troškova…</p>}
      {!loading &&
        result?.month === month &&
        (result.error ? (
          <>
            <p role="alert">{result.error}</p>
            <Button
              variant="secondary"
              onClick={() => {
                setResult(null);
                setReload((value) => value + 1);
              }}
            >
              Ponovno učitaj troškove
            </Button>
          </>
        ) : (
          <>
            {!result.sites.length && <p>Nema likvidiranih troškova za odabrani mjesec.</p>}
            <ul className="grid gap-3" aria-label="Neto troškovi gradilišta">
              {result.sites.map((site) => (
                <li key={site.id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <h3 className="font-semibold">{site.name}</h3>
                    <strong>{invoiceMoney(centsString(site.total))}</strong>
                  </div>
                  <dl className="invoice-facts">
                    {Object.entries(site.categories).map(([name, amount]) => (
                      <div key={name} className="contents">
                        <dt>{name}</dt>
                        <dd>{invoiceMoney(centsString(amount))}</dd>
                      </div>
                    ))}
                  </dl>
                </li>
              ))}
            </ul>
          </>
        ))}
    </CaseCard>
  );
}
